import { useState } from 'react'
import { Head, usePage } from '@inertiajs/react'
import { Github, Menu, X } from 'lucide-react'
import type { SharedPageProps } from '@/types/shared'
import CandidateHowItWorks from '@/components/Shared/CandidateHowItWorks'
import IndividualWhyHyrin from '@/components/Shared/IndividualWhyHyrin'
import { FlashBanner } from '@/components/Shared/FlashBanner'
import { HyrinWordmark } from '@/components/Shared/HyrinWordmark'
import { PublicFooter } from '@/components/Shared/PublicFooter'

type ActiveModal = 'howItWorks' | 'whyHyrin' | null

interface CandidatesLandingProps {
  providerLabel: string
  csrfToken: string
}

const PROOF_SOURCES = [
  { name: 'GitHub', detail: '212 commits · last 90 days' },
  { name: 'Hugging Face', detail: '3 models · 41k pulls' },
  { name: 'arXiv', detail: '2 papers · retrieval eval' },
  { name: 'Kaggle', detail: 'grandmaster · tabular' },
]

const THIS_WEEK = [
  { id: 'views', text: '3 organizations studied your proof' },
  { id: 'offer', text: '1 real offer — numbers included' },
]

const MANIFESTO_ITEMS = [
  {
    num: '01',
    statement: 'Hyrin shows where you fit.',
    exclamation: 'Don’t wait until you need a job!',
    body: 'Your proof is matched against live positions continuously — you always know which teams need exactly what you’ve been shipping, long before you’re “on the market”.',
  },
  {
    num: '02',
    statement: 'Keep getting real offers.',
    exclamation: 'Let the companies chase you!',
    body: 'Organizations search Hyrin and arrive with a real position and real numbers — not a “quick chat about an exciting opportunity”. You decide which ones deserve your time.',
  },
  {
    num: '03',
    statement: 'Stop applying as keywords.',
    exclamation: 'Get out of the rat race!',
    body: 'No ATS black hole, no tuning your résumé for a parser. You’re matched on verified recent work — commits, papers, models — not on whether you typed the right words in the right order.',
  },
  {
    num: '04',
    statement: 'Keep your Hyrin profile ready.',
    exclamation: 'Your next opportunity should not start from panic!',
    body: 'Connect your sources once; your proof stays current as you ship. The day you decide to move, you’re already visible — calm, prepared, and worth chasing.',
  },
]

const PLEDGE_TICKS = [
  { id: 'features', text: 'all features, day one' },
  { id: 'tier', text: 'no premium tier' },
  { id: 'paywall', text: 'no paywall when it matters most' },
]

interface GithubSignInFormProps {
  csrfToken: string
  providerLabel: string
  className: string
  compact?: boolean
}

function GithubSignInForm({ csrfToken, providerLabel, className, compact }: GithubSignInFormProps) {
  return (
    <form method="post" action="/auth/github" className="inline-flex">
      <input type="hidden" name="authenticity_token" value={csrfToken} />
      <button type="submit" className={className}>
        <Github className="size-5" aria-hidden="true" />
        {compact ? 'Sign in' : `Continue with ${providerLabel}`}
      </button>
    </form>
  )
}

const REVEAL =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:fill-mode-both motion-safe:duration-700'

function revealDelay(delayMs: number) {
  return { animationDelay: `${delayMs}ms` }
}

export default function CandidatesLanding({ providerLabel, csrfToken }: CandidatesLandingProps) {
  const { flash } = usePage<SharedPageProps>().props
  const [activeModal, setActiveModal] = useState<ActiveModal>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <>
      <Head title="Show us how you build · Hyrin" />

      <div className="min-h-screen bg-muted">
        <FlashBanner flash={flash} />
        {/* Header */}
        <header className="relative flex h-16 items-center justify-between border-b border-border px-5 sm:px-9">
          <a href="/" className="flex items-center gap-2">
            <HyrinWordmark />
            <span className="ml-3 hidden border-l border-border pl-3 font-mono text-[12px] text-muted-foreground lg:inline">
              Your agent for your AI jobs
            </span>
          </a>
          <div className="flex items-center gap-5">
            <nav className="hidden items-center gap-5 md:flex">
              <a
                href="#"
                className="font-sans text-[13px] font-semibold text-foreground underline-offset-2 hover:underline"
              >
                For individuals
              </a>
              <button
                onClick={() => setActiveModal('howItWorks')}
                className="font-sans text-[13px] text-muted-foreground transition-colors hover:text-foreground"
              >
                How it works?
              </button>
              <button
                onClick={() => setActiveModal('whyHyrin')}
                className="font-sans text-[13px] text-muted-foreground transition-colors hover:text-foreground"
              >
                Why Hyrin?
              </button>
            </nav>
            <a
              href="/for-organizations"
              className="flex h-[34px] items-center rounded-[8px] bg-foreground px-4 font-sans text-[13px] font-semibold text-background transition-opacity hover:opacity-90"
            >
              For Organizations
            </a>
            <button
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className="flex size-9 items-center justify-center rounded-[8px] border border-border text-foreground md:hidden"
            >
              {menuOpen ? <X className="size-[18px]" /> : <Menu className="size-[18px]" />}
            </button>
          </div>
          {menuOpen && (
            <nav className="absolute inset-x-0 top-16 z-20 flex flex-col border-b border-border bg-muted px-5 py-3 md:hidden">
              <a href="#" className="py-3 font-sans text-[14px] font-semibold text-foreground">
                For individuals
              </a>
              <button
                onClick={() => {
                  setMenuOpen(false)
                  setActiveModal('howItWorks')
                }}
                className="py-3 text-left font-sans text-[14px] text-muted-foreground"
              >
                How it works?
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false)
                  setActiveModal('whyHyrin')
                }}
                className="py-3 text-left font-sans text-[14px] text-muted-foreground"
              >
                Why Hyrin?
              </button>
            </nav>
          )}
        </header>

        {/* Hero */}
        <section className="grid grid-cols-1 gap-12 px-5 pb-20 pt-12 sm:px-10 lg:grid-cols-[1.05fr_420px] lg:pt-[76px]">
          <div className="flex flex-col justify-center">
            <p style={revealDelay(0)} className={`mb-4 font-mono text-[11px] uppercase tracking-eyebrow text-primary ${REVEAL}`}>
              For individuals
            </p>
            <h1
              style={revealDelay(80)}
              className={`mb-6 font-heading text-[42px] font-bold leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[52px] lg:text-[60px] ${REVEAL}`}
            >
              Show us how
              <br />
              you build.
            </h1>
            <div
              style={revealDelay(180)}
              className={`mb-6 flex items-baseline gap-3 font-mono text-[15px] text-foreground sm:text-[17px] ${REVEAL}`}
            >
              <span className="font-semibold text-primary">❯</span>
              <span>
                code is cheap — show us how you talk to LLMs.
                <span className="ml-[2px] text-primary motion-safe:animate-pulse">▍</span>
              </span>
            </div>
            <p
              style={revealDelay(280)}
              className={`mb-8 max-w-[460px] font-sans text-[17px] leading-[1.6] text-muted-foreground ${REVEAL}`}
            >
              Connect your sources — GitHub, arXiv, Hugging Face, Kaggle — and let verified work
              speak for you. Hyrin reads it as proof, and matches you to the positions that fit it.
            </p>
            <div style={revealDelay(380)} className={REVEAL}>
              <GithubSignInForm
                csrfToken={csrfToken}
                providerLabel={providerLabel}
                className="flex h-[54px] items-center justify-center gap-3 rounded-cta bg-foreground px-7 font-sans text-base font-semibold text-background shadow-sm transition-opacity hover:opacity-90"
              />
              <p className="mt-4 font-mono text-[12px] leading-[1.5] text-muted-foreground">
                We only read your public profile to confirm identity. Free — forever.
              </p>
            </div>
          </div>

          {/* Proof ledger card */}
          <div
            style={revealDelay(300)}
            className={`flex h-fit flex-col self-center rounded-card border border-border bg-background p-[20px] shadow-[0_4px_40px_rgba(20,40,32,0.08)] ${REVEAL}`}
          >
            <p className="mb-3 font-mono text-[11px] uppercase tracking-eyebrow text-muted-foreground">
              Your proof · live
            </p>
            <div className="flex flex-col">
              {PROOF_SOURCES.map((source) => (
                <div
                  key={source.name}
                  className="flex items-center justify-between border-b border-border py-[11px]"
                >
                  <div>
                    <p className="font-sans text-[14px] font-semibold text-foreground">
                      {source.name}
                    </p>
                    <p className="mt-[2px] font-mono text-[11px] text-muted-foreground">
                      {source.detail}
                    </p>
                  </div>
                  <span className="inline-flex rounded-full bg-secondary px-[10px] py-[4px] font-mono text-[10px] uppercase tracking-[0.08em] text-primary">
                    ✓ verified
                  </span>
                </div>
              ))}
            </div>
            <p className="mb-2 mt-4 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
              This week
            </p>
            <div className="flex flex-col gap-[8px]">
              {THIS_WEEK.map((item) => (
                <p key={item.id} className="flex items-center gap-[10px] font-sans text-[14px] text-foreground">
                  <span className="size-[5px] shrink-0 rounded-full bg-primary" />
                  {item.text}
                </p>
              ))}
            </div>
            <p className="mt-4 border-t border-border pt-3 font-mono text-[10px] text-muted-foreground">
              Kept current while you ship. Zero upkeep.
            </p>
          </div>
        </section>

        {/* Manifesto */}
        <section id="manifesto" className="bg-background py-[76px]">
          <div className="mx-auto max-w-[1100px] px-5 sm:px-10">
            <p className="mb-3 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
              The manifesto
            </p>
            <h2 className="mb-4 font-heading text-[30px] font-bold leading-[1.08] tracking-[-0.025em] text-foreground sm:text-[38px]">
              Built for the days you&apos;re not looking.
            </h2>
            <p className="mb-10 max-w-[560px] font-sans text-[16px] leading-[1.6] text-muted-foreground">
              Job hunting is a panic response. Hyrin replaces it with a standing position of
              strength — proof that stays live, offers that keep arriving.
            </p>
            <div className="flex flex-col">
              {MANIFESTO_ITEMS.map((item) => (
                <div
                  key={item.num}
                  className="grid grid-cols-1 gap-4 border-t border-border py-8 md:grid-cols-[64px_1.1fr_0.9fr] md:gap-8 md:py-10"
                >
                  <span className="font-mono text-[13px] font-semibold text-primary md:pt-[6px]">
                    {item.num}
                  </span>
                  <div>
                    <h3 className="font-heading text-[24px] font-bold leading-[1.12] tracking-[-0.02em] text-foreground sm:text-[30px]">
                      {item.statement}
                    </h3>
                    <p className="mt-2 font-sans text-[16px] font-semibold text-primary">
                      {item.exclamation}
                    </p>
                  </div>
                  <p className="font-sans text-[15px] leading-[1.65] text-muted-foreground md:pt-[6px]">
                    {item.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The pledge */}
        <section className="bg-foreground py-[80px]">
          <div className="mx-auto max-w-[760px] px-6 text-center sm:px-10">
            <p className="mb-4 font-mono text-[11px] uppercase tracking-eyebrow text-secondary">
              // the pledge
            </p>
            <h2 className="mb-6 font-heading text-[34px] font-bold leading-[1.06] tracking-[-0.025em] text-background sm:text-[44px]">
              Free for individuals.
              <br />
              Forever.
            </h2>
            <p className="mx-auto mb-9 max-w-[560px] font-sans text-[16px] leading-[1.7] text-background/70">
              Every candidate feature, for everyone, from day one. Career platforms like to charge
              you at your most desperate hour — the paywall appears exactly when you need a job. We
              call that ransom. On Hyrin, the day you need to move is the day we work hardest for
              you. For free.
            </p>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-8">
              {PLEDGE_TICKS.map((tick) => (
                <p key={tick.id} className="font-mono text-[12px] text-background/80">
                  <span className="mr-2 text-secondary">✓</span>
                  {tick.text}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-muted px-6 py-[84px] text-center">
          <p className="mb-3 font-mono text-[11px] uppercase tracking-eyebrow text-primary">
            Get started
          </p>
          <h2 className="mb-5 font-heading text-[32px] font-bold leading-[1.08] tracking-[-0.025em] text-foreground sm:text-[40px]">
            Your work already proves it.
          </h2>
          <p className="mx-auto mb-8 max-w-[420px] font-sans text-[16px] leading-[1.55] text-muted-foreground">
            Sign in, connect your sources, claim your username. Ten minutes today — no panic later.
          </p>
          <div className="flex justify-center">
            <GithubSignInForm
              csrfToken={csrfToken}
              providerLabel={providerLabel}
              className="flex h-[54px] items-center justify-center gap-3 rounded-cta bg-foreground px-8 font-sans text-base font-semibold text-background shadow-sm transition-opacity hover:opacity-90"
            />
          </div>
          <p className="mt-4 font-mono text-[12px] text-muted-foreground">
            We only read your public profile to confirm identity.
          </p>
        </section>

        <PublicFooter />
      </div>

      <CandidateHowItWorks isOpen={activeModal === 'howItWorks'} onClose={() => setActiveModal(null)} />
      <IndividualWhyHyrin isOpen={activeModal === 'whyHyrin'} onClose={() => setActiveModal(null)} />
    </>
  )
}
